import { memo } from 'react';
import { Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { getBlockMetadata } from 'redux/PlanPageSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_TOOLBAR_ARROW_TAB_WIDTH,
  PLAN_TOOLBAR_HEIGHT,
} from '../planPageRevampUi';

import PlanFilterGroupPill from './PlanFilterGroupPill';
import PlanToolbarArrowTabButton from './PlanToolbarArrowTabButton';
import PlanViewModeTabs from './PlanViewModeTabs';

export type PlanViewMode = 'chart-table' | 'chart' | 'table';

interface Properties {
  showInputsPanel: boolean;
  onToggleInputsPanel: () => void;
  showWhatIfsPanel: boolean;
  onToggleWhatIfsPanel: () => void;
  hasWhatIfsDrivers: boolean;
  showAnalytics?: boolean;
  viewMode: PlanViewMode;
  onViewModeChange: (mode: PlanViewMode) => void;
  refetchRequest: () => void;
}

const PlanToolbar = ({
  showInputsPanel,
  onToggleInputsPanel,
  showWhatIfsPanel,
  onToggleWhatIfsPanel,
  hasWhatIfsDrivers,
  showAnalytics = true,
  viewMode,
  onViewModeChange,
  refetchRequest,
}: Properties) => {
  const themeColors = useSelector(getPlanPageThemeColors);
  const metadata = useSelector(getBlockMetadata);
  const hasDimensionFilters = (metadata?.dimensions?.length ?? 0) > 0;

  return (
    <Flex
      w='100%'
      px={PLAN_HEADER_HORIZONTAL_PADDING}
      h={PLAN_TOOLBAR_HEIGHT}
      alignItems='center'
      gap={3}
      bg={themeColors.surface}
      borderBottom='1px solid'
      borderColor={themeColors.border}
    >
      <Flex alignItems='center' gap={3} minW={0} flexShrink={0}>
        <PlanToolbarArrowTabButton
          label='View Inputs'
          direction='left'
          width={PLAN_TOOLBAR_ARROW_TAB_WIDTH}
          isActive={showInputsPanel}
          activeBg={themeColors.activeBg}
          activeColor={themeColors.primary}
          borderColor={themeColors.border}
          textColor={themeColors.textDark}
          onClick={onToggleInputsPanel}
        />

        {hasDimensionFilters && <PlanFilterGroupPill refetchRequest={refetchRequest} />}
      </Flex>

      <Flex alignItems='stretch' gap={3} ml='auto' flexShrink={0} h='100%'>
        {showAnalytics && (
          <PlanViewModeTabs
            viewMode={viewMode}
            onChange={onViewModeChange}
            primaryColor={themeColors.primary}
            textColor={themeColors.textDark}
            activeBg={themeColors.activeBg}
          />
        )}

        {showAnalytics && hasWhatIfsDrivers && (
          <Flex alignItems='center' flexShrink={0}>
            <PlanToolbarArrowTabButton
              label='What Ifs'
              direction='right'
              width={PLAN_TOOLBAR_ARROW_TAB_WIDTH}
              isActive={showWhatIfsPanel}
              activeBg={themeColors.activeBg}
              activeColor={themeColors.primary}
              borderColor={themeColors.border}
              textColor={themeColors.textDark}
              onClick={onToggleWhatIfsPanel}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(PlanToolbar);
